import { IonContent, IonPage } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import MobilePreview from '../components/MobilePreview';
import ScaledFigmaCanvas from '../components/figma/ScaledFigmaCanvas';
import { FIGMA_BASE_HEIGHT, FIGMA_BASE_WIDTH } from '../components/figma/figma.constants';
import LessonPlayer from '../components/lesson/LessonPlayer';
import { fetchModuleLesson } from '../lib/lessons';
import { fetchModules } from '../lib/modules';
import type { CourseLesson } from '../types/lesson';
import type { CourseModule } from '../types/course';
import { useAuth } from '../context/AuthContext';

interface RouteParams {
  moduleId: string;
}

interface LocationState {
  module?: CourseModule;
}

const ModuleLesson: React.FC = () => {
  const { moduleId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const initialModule = location.state?.module ?? null;
  const { session, updateSessionScore } = useAuth();

  const [moduleInfo, setModuleInfo] = useState<CourseModule | null>(initialModule);
  const [lesson, setLesson] = useState<CourseLesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let nextModule = initialModule;
        if (!nextModule) {
          const modules = await fetchModules();
          nextModule = modules.find((item) => item.id === moduleId) ?? null;
        }

        const lessonData = await fetchModuleLesson(moduleId);

        if (cancelled) {
          return;
        }

        setModuleInfo(nextModule);
        setLesson(lessonData);

        if (!lessonData) {
          setError('Aucun contenu disponible pour ce module.');
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Impossible de charger le module');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [initialModule, moduleId]);

  const handleBackToDashboard = useCallback(() => {
    history.replace('/app/dashboard');
  }, [history]);

  const handleAwardPoints = useCallback(async (points: number) => {
    if (!session) {
      return;
    }
    const nextScore = session.score + points;
    await updateSessionScore(nextScore);
  }, [session, updateSessionScore]);

  const score = session?.score ?? 0;

  const mainContent = useMemo(() => {
    const renderStatus = (children: React.ReactNode) => (
      <div className="lesson-player">
        <div className="lesson-player__empty">{children}</div>
      </div>
    );

    if (loading) {
      return renderStatus(<h2>Chargement du cours…</h2>);
    }

    if (error) {
      return renderStatus(
        <>
          <h2>Oups…</h2>
          <p>{error}</p>
          <button type="button" className="lesson-player__primary-btn" onClick={handleBackToDashboard}>
            Retour au tableau de bord
          </button>
        </>
      );
    }

    if (!lesson) {
      return renderStatus(
        <>
          <h2>Aucun contenu</h2>
          <p>Revenez plus tard, le module est en preparation.</p>
          <button type="button" className="lesson-player__primary-btn" onClick={handleBackToDashboard}>
            Retour au tableau de bord
          </button>
        </>
      );
    }

    return (
      <LessonPlayer
        moduleTitle={moduleInfo?.title ?? 'Module'}
        lesson={lesson}
        score={score}
        onBack={handleBackToDashboard}
        onFinish={handleBackToDashboard}
        onAwardPoints={handleAwardPoints}
      />
    );
  }, [error, handleAwardPoints, handleBackToDashboard, lesson, loading, moduleInfo?.title, score]);

  return (
    <IonPage>
      <IonContent fullscreen className="home-content">
        <MobilePreview ariaLabel="Module en cours">
          <ScaledFigmaCanvas
            baseWidth={FIGMA_BASE_WIDTH}
            baseHeight={FIGMA_BASE_HEIGHT}
            ariaLabel={moduleInfo?.title ?? 'Lecture du module'}
          >
            {mainContent}
          </ScaledFigmaCanvas>
        </MobilePreview>
      </IonContent>
    </IonPage>
  );
};

export default ModuleLesson;
